import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CalendarAppointmentsComponent } from '../../components/calendars/calendar-appointments/calendar-appointments.component';
import { CalendarAppointmentsService } from '../../_services/calendars/calendar-appointments.service';
import { CalendarSettingsService } from '../../_services/calendar-settings/calendar-settings.service';
import IAppointment from '../../_interfaces/IAppointment';
import ICalendar from '../../_interfaces/ICalendar';
import { Subscription } from 'rxjs';
import IPatient from '../../_interfaces/IPatients';
import IPatientHasTreatment from '../../_interfaces/IPatientHasTreatment';
import { BaseComponent } from '../base/base.component';
import { SessionService } from '../../_services/session/session.service';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MessagesModule } from 'primeng/messages';
import { SelectorCalendarModeComponent } from '../selectors/selector-calendar-mode/selector-calendar-mode.component';
import { SelectorMultiCalendarComponent } from '../selectors/selector-multi-calendar/selector-multi-calendar.component';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import {
  ICalendarSettings,
  IVisibilityFilter,
  createDefaultSettings,
  countActiveShowFilters,
} from '../../_interfaces/ICalendarSettings';

@Component({
  standalone: true,
  selector: 'app-calendar-appointments-wrapper',
  templateUrl: './calendar-appointments-wrapper.component.html',
  styleUrls: ['./calendar-appointments-wrapper.component.scss'],
  imports: [
    DialogModule,
    CheckboxModule,
    SelectorMultiCalendarComponent,
    FormsModule,
    TranslatePipe,
    CalendarAppointmentsComponent,
    CommonModule,
    MessagesModule,
    TooltipModule,
    ButtonModule,
  ],
})
export class CalendarAppointmentsWrapperComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input('patient')
  public patient?: IPatient = undefined;
  @Input('treatments')
  public treatments?: IPatientHasTreatment;
  @Input('quickMode')
  public quickMode: boolean = false;
  @Input('date')
  public date: string = '';

  @ViewChild('calendarModeSelector')
  public calendarModeSelector: SelectorCalendarModeComponent;

  @ViewChild('calendarSelector')
  public calendarSelector: SelectorMultiCalendarComponent;

  @ViewChild('appointmentsCalendar')
  public appointmentsCalendar: CalendarAppointmentsComponent;

  public updateAppointment: (appointment: IAppointment) => void;
  public subscriptionCalendar: Subscription;
  public mode: { name: any; code: any };
  public calendars: ICalendar[];

  public loading = true;

  /** Configuración completa del calendario */
  public settings: ICalendarSettings = createDefaultSettings();
  public settingsDialogVisible = false;

  openSettingsDialog(): void {
    this.settingsDialogVisible = true;
  }

  closeSettingsDialog(): void {
    this.settingsDialogVisible = false;
  }

  resetSettingsToDefault(): void {
    this.calendarSettingsService.resetSettings().subscribe((defaultSettings) => {
      this.settings = defaultSettings;
    });
  }

  /**
   * Manejador de cambio de filtro
   */
  onFilterChange(filter: IVisibilityFilter, newValue: boolean): void {
    // Crear una copia profunda de los filtros para que Angular detecte el cambio
    const newFilters = this.settings.filters.map((f) =>
      f.id === filter.id ? { ...f, active: newValue } : { ...f },
    );

    this.settings = {
      ...this.settings,
      filters: newFilters,
    };

    // Persistir cambios
    this.persistSettings();
  }

  /**
   * Manejador de cambio de configuración de vista
   */
  onViewSettingChange<K extends keyof ICalendarSettings['view']>(
    key: K,
    value: ICalendarSettings['view'][K],
  ): void {
    this.settings = {
      ...this.settings,
      view: { ...this.settings.view, [key]: value },
    };

    // Persistir cambios
    this.persistSettings();
  }

  /**
   * Manejador de cambio de configuración de display
   */
  onDisplaySettingChange<K extends keyof ICalendarSettings['display']>(
    key: K,
    value: ICalendarSettings['display'][K],
  ): void {
    this.settings = {
      ...this.settings,
      display: { ...this.settings.display, [key]: value },
    };

    // Persistir cambios
    this.persistSettings();
  }

  /**
   * Persiste la configuración actual usando el servicio
   */
  private persistSettings(): void {
    this.calendarSettingsService.saveSettings(this.settings).subscribe();
  }

  /**
   * Cuenta los filtros 'show' activos
   */
  get activeFiltersCount(): number {
    return countActiveShowFilters(this.settings.filters);
  }

  /**
   * Retorna true si hay filtros 'show' activos
   */
  get hasActiveFilters(): boolean {
    return this.activeFiltersCount > 0;
  }

  constructor(
    public session: SessionService,
    private calendarAppointments: CalendarAppointmentsService,
    private calendarSettingsService: CalendarSettingsService,
  ) {
    super(session);
  }

  ngOnDestroy() {
    this.subscriptionCalendar.unsubscribe();
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Cargar configuración guardada
    this.calendarSettingsService.getSettings().subscribe((savedSettings) => {
      this.settings = savedSettings;
    });

    this.subscriptionCalendar = this.calendarAppointments
      .getCalendar$()
      .subscribe((calendars: ICalendar[]) => {
        this.calendars = calendars;
        this.loading = false;
      });
  }
}
